import React, {useState} from "react";
import {ChevronLeft, ChevronRight} from "lucide-react";

export interface AppuntamentoCalendarProps {
    selectedDate: Date | null;
    onSelect: (date: Date) => void;
    isDateSelectable?: (date: Date) => boolean;
}

const AppuntamentoCalendar = ({selectedDate, onSelect, isDateSelectable}: AppuntamentoCalendarProps) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 30);

    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());

    const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

    const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year: number, month: number) => {
        const firstDay = new Date(year, month, 1).getDay();
        return firstDay === 0 ? 6 : firstDay - 1;
    };

    const isDateDisabled = (date: Date) => {
        if (date < today || date > maxDate) {
            return true;
        }

        if (isDateSelectable) {
            return !isDateSelectable(date);
        }

        return false;
    };
    const canGoPrev = viewYear > today.getFullYear() || viewMonth > today.getMonth();
    const canGoNext = new Date(viewYear, viewMonth + 1, 1) <= maxDate;

    const handlePrevMonth = () => {
        if (!canGoPrev) return;
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear(y => y - 1);
        } else {
            setViewMonth(m => m - 1);
        }
    };

    const handleNextMonth = () => {
        if (!canGoNext) return;
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear(y => y + 1);
        } else {
            setViewMonth(m => m + 1);
        }
    };

    const firstDayOffset = getFirstDayOfMonth(viewYear, viewMonth);
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const cells: (Date | null)[] = [
        ...Array(firstDayOffset).fill(null),
        ...Array.from({length: daysInMonth}, (_, i) => new Date(viewYear, viewMonth, i + 1)),
    ];

    return (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
                <button
                    type="button"
                    onClick={handlePrevMonth}
                    disabled={!canGoPrev}
                    className={`p-1.5 rounded-lg transition-colors ${canGoPrev ? 'hover:bg-slate-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
                >
                    <ChevronLeft className="h-4 w-4"/>
                </button>
                <span className="font-semibold text-gray-900 text-sm">{monthNames[viewMonth]} {viewYear}</span>
                <button
                    type="button"
                    onClick={handleNextMonth}
                    disabled={!canGoNext}
                    className={`p-1.5 rounded-lg transition-colors ${canGoNext ? 'hover:bg-slate-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
                >
                    <ChevronRight className="h-4 w-4"/>
                </button>
            </div>
            <div className="grid grid-cols-7 mb-1">
                {dayNames.map(d => (
                    <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
                {cells.map((date, idx) => {
                    if (!date) return <div key={`e-${idx}`}/>;
                    const disabled = isDateDisabled(date);
                    const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                    const isToday = date.toDateString() === today.toDateString();
                    return (
                        <button
                            key={date.toISOString()}
                            type="button"
                            onClick={() => !disabled && onSelect(date)}
                            disabled={disabled}
                            className={[
                                'w-full aspect-square flex items-center justify-center text-xs rounded-lg transition-colors font-medium',
                                isSelected ? 'bg-primary text-white' : '',
                                isToday && !isSelected ? 'border border-primary text-primary' : '',
                                disabled ? 'text-gray-300 cursor-not-allowed' : (!isSelected ? 'hover:bg-slate-100 text-gray-700 cursor-pointer' : ''),
                            ].filter(Boolean).join(' ')}
                        >
                            {date.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default AppuntamentoCalendar;
